import { Link } from 'react-router-dom';
import { Shield, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "December 26, 2025";
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-medical-primary text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8" />
            <h1 className="text-4xl font-heading font-semibold">Privacy Policy</h1>
          </div>
          <p className="text-white/90">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Medical Supply and More ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and purchase our medical supply products. Please read this policy carefully. By using our website, you consent to the practices described in this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Information We Collect</h2>
            
            <h3 className="text-lg font-semibold mt-4 mb-2">Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may collect personal information that you voluntarily provide when you:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Create an account on our website</li>
              <li>Place an order for products</li>
              <li>Subscribe to our newsletter</li>
              <li>Contact us with inquiries or feedback</li>
              <li>Participate in promotions or surveys</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              This information may include your name, email address, phone number, shipping address, billing address, and payment information.
            </p>

            <h3 className="text-lg font-semibold mt-6 mb-2">Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed">
              When you visit our website, we may automatically collect certain information about your device, including your IP address, browser type, operating system, access times, and the pages you have viewed. We may also collect information about how you interact with our website using cookies and similar tracking technologies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We use the information we collect for various purposes, including to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Process and fulfill your orders, including shipping and delivery</li>
              <li>Send you order confirmations, shipping notifications, and receipts</li>
              <li>Communicate with you about products, services, and promotions</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Improve our website, products, and services</li>
              <li>Detect and prevent fraud or unauthorized activities</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Information Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf, such as payment processing, shipping, and email delivery.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid requests by public authorities.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.</li>
              <li><strong>Protection of Rights:</strong> We may disclose information to protect our rights, privacy, safety, or property, and that of our customers and others.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Payment Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We use secure, industry-standard payment processing services to handle your payment information. We do not store your complete credit card information on our servers. All payment transactions are encrypted using SSL (Secure Socket Layer) technology to ensure your financial information remains protected.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed">
              We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookies through your browser settings. Please note that disabling cookies may affect some features of our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. We will securely delete or anonymize your information when it is no longer needed.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Your Rights and Choices</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>The right to access the personal information we hold about you</li>
              <li>The right to request correction of inaccurate information</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to opt-out of marketing communications</li>
              <li>The right to withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website is not intended for children under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately so we can delete such information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <div className="space-y-2 text-gray-700">
              <p className="font-semibold">Medical Supply and More</p>
              <p>Orlando, FL</p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-medical-primary" />
                <a href="mailto:info@medicalsupplyandmore.com" className="text-medical-primary hover:underline">
                  info@medicalsupplyandmore.com
                </a>
              </p>
              <p>Phone: (407) 415-3024</p>
            </div>
          </section>

          <div className="pt-6 border-t text-center">
            <Link to="/" className="text-medical-primary hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
